// Angular
import { Component, Inject, OnDestroy, OnInit } from '@angular/core';

// Libraries
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Select, Store } from '@ngxs/store';
import { Observable, take, takeUntil } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';

// Service
import { CoreService } from '../../shared/services';

// Models
import { AppCleanup } from '../../shared/models';

// Store
import { StorageState } from '../../shared/state/storage.state';
import { StorageActions } from '../../shared/state/storage.action';

// Helpers
import { UtilityHelper } from '../../shared/helpers';

// Enums
import { StatusType } from '../../shared/enums';

@Component({
	selector: 'theme-settings-dialog',
	templateUrl: './theme-settings-dialog.component.html',
	styleUrls: ['./theme-settings-dialog.component.scss'],
})
export class ThemeSettingsDialogComponent extends AppCleanup implements OnInit, OnDestroy {
	componentTitle: string = 'Theme settings';
	@Select(StorageState.darkMode) darkMode$: Observable<boolean>;
	darkMode: boolean = false;
	mode: ModeType; // Doing this because mat-button-group has a bug when using boolean value, that's why I have to use an enum
	darkModeCopy: boolean = false;
	@Select(StorageState.brightness) brightness$: Observable<string>;
	brightness: string;
	brightnessCopy: string;

	constructor(
		private dialogRef: MatDialogRef<ThemeSettingsDialogComponent>,
		private store: Store,
		private coreService: CoreService,
		private translateService: TranslateService,
		@Inject(MAT_DIALOG_DATA) public data: { title: string; label: string; action: string; existingValue: string }
	) {
		super();
	}

	ngOnInit() {
		this.darkMode$.pipe(takeUntil(this.destroy), take(1)).subscribe((darkMode) => {
			this.darkModeCopy = UtilityHelper.deepCopy(darkMode);
		});

		this.brightness$.pipe(takeUntil(this.destroy), take(1)).subscribe((brightness) => {
			this.brightnessCopy = UtilityHelper.deepCopy(brightness);
			// Initialize the brightness property only once
			this.brightness = UtilityHelper.deepCopy(brightness);
		});

		this.darkMode$.pipe(takeUntil(this.destroy)).subscribe((darkMode) => {
			this.darkMode = UtilityHelper.deepCopy(darkMode);

			if (darkMode) {
				this.mode = ModeType.DARK;
			} else {
				this.mode = ModeType.LIGHT;
			}
		});

		// Remove the continuous brightness subscription to prevent feedback loop
		// The brightness property will be updated directly in changeBrightness method

		this.dialogRef.backdropClick().subscribe(() => {
			this.cancel();
		});
	}

	ngOnDestroy() {
		super.ngOnDestroy();
	}

	isLightMode() {
		return true;
	}

	changeBrightness(event) {
		let value = (event.target as HTMLInputElement).value;
		// Update local property immediately for smooth UI interaction
		this.brightness = value;
		// Dispatch to store to update the global state
		this.store.dispatch(new StorageActions.SetAppBrightness(value));
	}

	changeMode(modeType) {
		this.mode = modeType;
		let value: boolean;

		if (this.mode === ModeType.DARK) {
			value = true;
		} else {
			value = false;
		}

		this.store.dispatch(new StorageActions.SetDarkMode(value));
	}

	get ModeType() {
		return ModeType;
	}

	saveDisabled() {
		if (this.brightness === this.brightnessCopy && this.darkMode === this.darkModeCopy) {
			return true;
		}

		return false;
	}

	cancel() {
		this.store.dispatch(new StorageActions.SetAppBrightness(this.brightnessCopy));
		this.store.dispatch(new StorageActions.SetDarkMode(this.darkModeCopy));
	}

	submit() {
		this.dialogRef.close();
		const message = `${this.translateService.instant('GENERIC.UPDATED', { name: this.translateService.instant('MODULE.THEME.TITLE_CAPITAL') })}`;
		this.coreService.toast({ message: message, type: StatusType.SUCCESS });
	}
}

export enum ModeType {
	DARK = 'Dark',
	LIGHT = 'Light',
}
